import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import { ParamMap } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { SessionState } from '@platform-frontend/account';
import { AssetState, DownloadLatestInvoice, ProductState, ShoppingCartPaymentState, ShoppingCartState } from '@platform-frontend/checkout';
import { SubscriptionBookingStatus } from '@platform-frontend/checkout-drop-in';
import { Asset, LicenseType, MARKETPLACE_MATOMO_EVENT_CATEGORY_AFTER_SALES, PaymentMethodInfo, ProductCategory } from '@platform-frontend/marketplace';
import { CountdownPageDirective } from '@platform-frontend/ui-tools';
import { ClickCallbackLink, CloseWindow, SimpleRouterState } from '@platform-frontend/util';
import { MatomoTracker } from 'ngx-matomo-client';
import { EMPTY, Observable, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';

@Component({
  selector: 'yat-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss']
})
export class ConfirmationComponent extends CountdownPageDirective implements OnInit {
  constructor(ngZone: NgZone, ref: ChangeDetectorRef, private store: Store, private matomoTracker: MatomoTracker) {
    super(ngZone, ref);
  }

  @Select(SessionState.email)
  sessionEmail$: Observable<string | null>;

  @Select(ShoppingCartState.selectedLicenseType)
  licenseType$: Observable<LicenseType | null>;

  @Select(ShoppingCartPaymentState.selectedPaymentMethod)
  selectedPaymentMethod$: Observable<PaymentMethodInfo | null>;

  @Select(SimpleRouterState.queryParamMap)
  queryParams$: Observable<ParamMap>;

  @Select(AssetState.asset)
  asset$: Observable<Asset | null>;

  @Select(ProductState.productCategory)
  productCategory$: Observable<ProductCategory | null>;

  @Select(AssetState.subscriptionBookingStatus)
  subscriptionBookingStatus$: Observable<SubscriptionBookingStatus | null>;

  readonly invoiceLink: ClickCallbackLink = { onClick: () => this.downloadInvoice(), class: 'text-p-40 invoice', id: 'ada43fe7-a4d8-4358-896a-4347f65cb20e' };
  readonly MATOMO_EVENT_CATEGORY_AFTER_SALES = MARKETPLACE_MATOMO_EVENT_CATEGORY_AFTER_SALES;

  subscriptionWasRenewed$: Observable<boolean>;
  showDownloadSection$: Observable<boolean>;

  @ViewChild('passwordInput', { static: false }) passwordInput: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.subscriptionWasRenewed$ = this.queryParams$.pipe(map(params => params.get('reason') === 'renew'));

    this.showDownloadSection$ = this.asset$.pipe(map(asset => asset?.product.productPaths.some(path => path.type === 'DOWNLOAD_PAGE') ?? false));
  }

  close(): void {
    this.store.dispatch(new CloseWindow());
  }

  private downloadInvoice(): void {
    combineLatest([this.licenseType$])
      .pipe(
        take(1),
        switchMap(([licenseType]) =>
          licenseType
            ? this.store
                .dispatch(
                  new DownloadLatestInvoice(licenseType.licenseTypeId, this.store.selectSnapshot(ShoppingCartPaymentState.selectedPaymentMethod)?.type ?? null)
                )
                .pipe(
                  tap(() => {
                    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_AFTER_SALES, 'Select_Download', 'InvoiceDownloadSuccess');
                  }),
                  catchError(() => {
                    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_AFTER_SALES, 'Select_Download', 'InvoiceDownloadFailed');
                    return of(null);
                  })
                )
            : EMPTY
        )
      )
      .subscribe();
  }
}
